import type { ReactNode } from 'react';
import ModuleLayout, { type BreadcrumbItem, type ModuleMetric } from '@/components/admin/ModuleLayout';
import { EntityDetailNavLayout, type EntityDetailNavGroup } from '@/components/admin/EntityDetailNav';
import { RecordSaveStatusBar, type RecordSaveStatus } from '@/components/admin/RecordSaveStatusBar';
import { CalenderIcon, CheckCircleIcon, DocsIcon, GroupIcon, InfoIcon, ListIcon, ShootingStarIcon } from '@/icons';

export type EventTabKey = 'info' | 'equipos' | 'configuracion' | 'podio' | 'puntaje' | 'clasificacion' | 'lanzamiento';

const EVENT_TAB_LABELS: Record<EventTabKey, string> = {
  info: 'Información',
  equipos: 'Equipos',
  configuracion: 'Configuración',
  podio: 'Podio',
  puntaje: 'Puntaje',
  clasificacion: 'Clasificación',
  lanzamiento: 'Control de lanzamiento',
};

interface EventShellProps {
  mode: 'create' | 'edit';
  activeTab: EventTabKey;
  onTabChange: (tab: EventTabKey) => void;
  breadcrumbs: BreadcrumbItem[];
  title: string;
  subtitle: string;
  metrics?: ModuleMetric[];
  headerActions?: ReactNode;
  tabLabels?: Partial<Record<EventTabKey, string>>;
  /**
   * Estado del guardado optimista por campo, provisto por quien renderiza el tab activo
   * (ej. el formulario de "Información"). Si se omite, no se muestra la barra de estado —
   * no todos los tabs tienen mutations que reportar todavía.
   */
  saveStatus?: RecordSaveStatus;
  onForceSync?: () => void;
  children: ReactNode;
}

// Shared shell for national Event create/view/edit (cccd-ux-redesign —
// cccd-admin-tabbed-shell): reused as-is by RegionalShell's tab-bar pattern.
export function EventShell({ mode, activeTab, onTabChange, breadcrumbs, title, subtitle, metrics, headerActions, tabLabels, saveStatus, onForceSync, children }: EventShellProps) {
  const locked = (key: EventTabKey) => mode === 'create' && key !== 'info';
  const label = (key: EventTabKey) => tabLabels?.[key] ?? EVENT_TAB_LABELS[key];
  const groups: EntityDetailNavGroup<EventTabKey>[] = [
    { label: 'GENERAL', items: [
      { key: 'info', label: label('info'), locked: locked('info'), icon: <InfoIcon /> },
      { key: 'configuracion', label: label('configuracion'), locked: locked('configuracion'), icon: <DocsIcon /> },
    ] },
    { label: 'COMPETENCIA', items: [
      { key: 'equipos', label: label('equipos'), locked: locked('equipos'), icon: <GroupIcon /> },
      { key: 'puntaje', label: label('puntaje'), locked: locked('puntaje'), icon: <ShootingStarIcon /> },
      { key: 'clasificacion', label: label('clasificacion'), locked: locked('clasificacion'), icon: <ListIcon /> },
      { key: 'lanzamiento', label: label('lanzamiento'), locked: locked('lanzamiento'), icon: <CalenderIcon /> },
      { key: 'podio', label: label('podio'), locked: locked('podio'), icon: <CheckCircleIcon /> },
    ] },
  ];

  return (
    <ModuleLayout
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      tabs={[
        { label: 'Panel', to: '/cccd' },
        { label: 'Nacionales', to: '/cccd/events', active: true },
        { label: 'Regionales', to: '/cccd/regionals' },
      ]}
      metrics={metrics ?? []}
      actions={headerActions}
    >
      <EntityDetailNavLayout
        groups={groups}
        activeKey={activeTab}
        onChange={(key) => !locked(key) && onTabChange(key)}
        tip="Los cambios en las fechas de cierre de inscripciones se reflejan en tiempo real para todos los participantes registrados."
      >
        {mode === 'create' ? (
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Las demás secciones (configuración, equipos, puntaje, podio…) se habilitan al crear el evento.
          </p>
        ) : null}
        {children}
        {saveStatus ? <RecordSaveStatusBar status={saveStatus} onForceSync={onForceSync} /> : null}
      </EntityDetailNavLayout>
    </ModuleLayout>
  );
}
